import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MoreVertical } from 'lucide-react';
import decorativeFabric from '../assets/homepage/decorative-fabric.jpg';
import decorativeSewingMachine from '../assets/homepage/decorative-sewing-machine.jpg';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section className="relative bg-white overflow-hidden" style={{ minHeight: '606px' }}>
      <div className="max-w-container-lg mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-manrope text-body-small text-brand-yellow"
            >
              Stitch. Manage. Grow.
            </motion.div>

            {/* Headline */}
            <h1 className="font-opensans text-display-xl text-text-primary font-bold">
              Master Ji
            </h1>

            {/* Description */}
            <div className="font-manrope text-body-xl text-text-primary space-y-4 max-w-[470px]">
              <p>
                We are the dedicated space where custom craftsmanship meets modern simplicity.
              </p>
              <p>
                Creators get the powerful tools to manage and grow their artistry. Customers get a single, free, seamless platform to track orders and manage their measurements.
              </p>
              <p>
                Preserving tradition. Perfecting the process.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/auth?mode=signup')}
                className="bg-brand-blue text-brand-cream font-manrope text-body-large px-8 py-3 rounded-3xl hover:bg-brand-blue/90 transition-all duration-300 flex items-center justify-center gap-2"
                style={{ minWidth: '178px', height: '52px' }}
              >
                Get Started
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/about')}
                className="bg-transparent text-text-light font-manrope text-body-large px-8 py-3 rounded-3xl hover:text-text-primary transition-all duration-300"
                style={{ minWidth: '179px', height: '52px' }}
              >
                Learn more
              </motion.button>
            </div>
          </motion.div>

          {/* Right Content - Stats Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
            style={{ minHeight: '500px', position: 'relative', width: '520px' }}
          >
            {/* Top Left Decorative Image Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute rounded-2xl overflow-hidden shadow-lg"
              style={{
                width: '180px',
                height: '140px',
                left: '0',
                top: '0',
              }}
            >
              <img 
                src={decorativeFabric} 
                alt="Decorative fabric"
                className="w-full h-full object-cover"
              />
            </motion.div>

            {/* Eliminate Card - Top Right */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute rounded-xl shadow-card"
              style={{
                width: '280px',
                height: '160px',
                left: '200px',
                top: '0',
                backgroundColor: '#f5f9f9',
                padding: '20px',
              }}
            >
              <div className="font-manrope text-[14px] text-text-primary font-semibold mb-1">
                Eliminate
              </div>
              <div className="font-opensans text-[48px] leading-[56px] text-text-dark font-bold mb-1">
                100%
              </div>
              <div className="font-manrope text-[13px] text-text-primary leading-tight">
                of time spent on paper-based filing and archive management.
              </div>
            </motion.div>

            {/* Time Saved Card - Bottom Left */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="absolute rounded-2xl overflow-hidden shadow-card"
              style={{
                width: '280px',
                height: '320px',
                left: '0',
                top: '160px',
                backgroundColor: '#fff8eb',
              }}
            >
              {/* Menu Icon */}
              <div className="absolute top-4 right-4 z-10">
                <MoreVertical className="w-5 h-5 text-text-muted" />
              </div>

              {/* Image at Top */}
              <div className="relative w-full h-[120px] overflow-hidden">
                <img 
                  src={decorativeFabric} 
                  alt="Time saved illustration"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Progress Indicator */}
              <div className="relative w-full flex justify-center pt-6 pb-2">
                <svg 
                  width="140" 
                  height="70" 
                  viewBox="0 0 140 70" 
                  className="overflow-visible"
                >
                  {/* Background arc */}
                  <path
                    d="M 15 60 A 55 55 0 0 1 125 60"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                  {/* Filled arc (60%) */}
                  <path
                    d="M 15 60 A 55 55 0 0 1 81 15"
                    fill="none"
                    stroke="#ffc857"
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                </svg>
              </div>

              {/* Text Content */}
              <div className="text-center px-4 pb-4">
                <div className="font-manrope text-[14px] text-text-muted mb-1">
                  Time Saved on Order Intake
                </div>
                <div className="font-opensans text-[28px] leading-[36px] text-text-dark font-bold">
                  60%
                </div>
              </div>
            </motion.div>

            {/* Bottom Right Decorative Image Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="absolute rounded-2xl overflow-hidden shadow-lg"
              style={{
                width: '200px',
                height: '220px',
                left: '300px',
                top: '180px',
              }}
            >
              <img 
                src={decorativeSewingMachine} 
                alt="Decorative sewing machine"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
